import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { LeaseNavigator } from "@/components/LeaseNavigator";
import { LeaseRenewalComparison } from "@/components/LeaseRenewalComparison";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Vault, Upload, FileText, Mail, ReceiptText, Shield, Folder, Loader2, Trash2, ExternalLink, MessageSquare, Compass,
} from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { key: "lease", label: "Lease", Icon: FileText },
  { key: "letter", label: "Letters", Icon: Mail },
  { key: "correspondence", label: "Correspondence", Icon: MessageSquare },
  { key: "receipt", label: "Receipts", Icon: ReceiptText },
  { key: "insurance", label: "Insurance", Icon: Shield },
  { key: "other", label: "Other", Icon: Folder },
];
const CAT_MAP = Object.fromEntries(CATS.map((c) => [c.key, c]));
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function PropertyVault({ propertyId }) {
  const { openUpgrade, isPremium } = useTier();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [navFor, setNavFor] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/properties/${propertyId}/vault`); setData(data); }
    catch (e) { console.error("load vault failed", e); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const openItem = (it) => {
    if (it.source === "upload") window.open(`${API}/vault/${it.doc_id}/file`, "_blank");
    else if (it.source === "lease") window.open(`${API}/properties/${propertyId}/lease-file`, "_blank");
    else if (it.source === "letter") setPreview(it);
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this document? This can't be undone.")) return;
    try { await api.delete(`/vault/${it.doc_id}`); toast.success("Document deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  if (loading || !data) return null;
  const { documents, usage, plan } = data;
  const grouped = CATS.map((c) => ({ ...c, items: documents.filter((d) => d.category === c.key) })).filter((g) => g.items.length > 0);
  const atCap = usage.limit !== null && usage.used >= usage.limit;
  const leaseCount = documents.filter((d) => d.source === "upload" && d.category === "lease").length;

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="property-vault">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><Vault size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold">Vault</h3>
            <p className="text-sm text-white/60">Your secure home for this tenancy's documents — lease, letters, receipts, insurance and landlord correspondence.</p>
          </div>
        </div>
        <Button size="sm" data-testid="vault-upload-btn"
          onClick={() => (atCap ? openUpgrade("document_vault") : setUploadOpen(true))}
          className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0">
          <Upload size={15} className="mr-1.5" /> Upload
        </Button>
      </div>

      {leaseCount >= 2 && (
        <Button size="sm" variant="outline" data-testid="vault-compare-leases-btn"
          onClick={() => (isPremium ? setCompareOpen(true) : openUpgrade("lease_navigator"))}
          className="mt-3 rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10">
          <Compass size={15} className="mr-1.5 text-brand" /> Compare lease versions
        </Button>
      )}

      {usage.limit !== null && (
        <div className="mt-3 flex items-center gap-3" data-testid="vault-usage-meter">
          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }} />
          </div>
          <span className="text-xs font-semibold text-white/70 shrink-0">{usage.used} of {usage.limit} documents</span>
        </div>
      )}
      {usage.limit === null && <p className="mt-2 text-xs text-emerald-300" data-testid="vault-usage-meter">Unlimited documents · TenantSeal Plus</p>}

      {grouped.length === 0 ? (
        <p className="mt-4 text-sm text-white/45" data-testid="vault-empty">No documents yet. Upload your lease, receipts, insurance or correspondence to keep them safe.</p>
      ) : (
        <div className="mt-4 space-y-4">
          {grouped.map((g) => (
            <div key={g.key} data-testid={`vault-cat-${g.key}`}>
              <p className="text-xs uppercase tracking-wide text-white/40 flex items-center gap-1.5"><g.Icon size={13} /> {g.label}</p>
              <div className="mt-1.5 space-y-1.5">
                {g.items.map((it) => {
                  const C = CAT_MAP[it.category] || CAT_MAP.other;
                  return (
                    <div key={it.id} data-testid={`vault-item-${it.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5">
                      <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/10 text-white/80 shrink-0"><C.Icon size={16} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{it.filename}</p>
                        <p className="text-[11px] text-white/45">
                          {fmt(it.uploaded_at)}
                          {it.source !== "upload" && <span className="ml-1.5 text-brand">· auto-filed</span>}
                          {it.note && <span className="ml-1.5 text-white/55">· {it.note}</span>}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" data-testid={`vault-open-${it.id}`} onClick={() => openItem(it)}
                        className="rounded-lg h-8 w-8 p-0 text-white/70 hover:bg-white/10" title="Open"><ExternalLink size={14} /></Button>
                      {it.source === "upload" && it.category === "lease" && (
                        <Button size="sm" variant="ghost" data-testid={`vault-navigator-${it.id}`}
                          onClick={() => (isPremium ? setNavFor(it.doc_id) : openUpgrade("lease_navigator"))}
                          className="rounded-lg h-8 w-8 p-0 text-brand hover:bg-white/10" title="Lease Navigator"><Compass size={14} /></Button>
                      )}
                      {it.source === "upload" && (
                        <Button size="sm" variant="ghost" data-testid={`vault-delete-${it.id}`} onClick={() => remove(it)}
                          className="rounded-lg h-8 w-8 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40" title="Delete"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadDialog propertyId={propertyId} onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); load(); }}
          onLimit={() => { setUploadOpen(false); openUpgrade("document_vault"); }} />
      )}
      {navFor && <LeaseNavigator docId={navFor} onClose={() => { setNavFor(null); load(); }} />}
      <LeaseRenewalComparison propertyId={propertyId} open={compareOpen} onOpenChange={setCompareOpen} />
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent data-testid="vault-preview-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{preview?.filename}</DialogTitle></DialogHeader>
          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#0F172A] whitespace-pre-wrap max-h-96 overflow-y-auto">{preview?.preview_text || "No preview available."}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UploadDialog({ propertyId, onClose, onUploaded, onLimit }) {
  const [category, setCategory] = useState("receipt");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    if (!file) { toast.error("Choose a file."); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", category);
      if (note.trim()) form.append("note", note.trim());
      await api.post(`/properties/${propertyId}/vault`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to vault");
      onUploaded();
    } catch (err) {
      if (err?.response?.status === 402) { onLimit(); return; }
      toast.error(err?.response?.data?.detail || "Couldn't upload.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="vault-upload-dialog">
        <DialogHeader>
          <DialogTitle>Add to vault</DialogTitle>
          <DialogDescription>PDF or image, up to 10MB.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Category</Label>
            <select data-testid="vault-category-select" value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
              {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
            data-testid="vault-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full rounded-xl" data-testid="vault-pick-btn">
            <Upload size={16} className="mr-2" /> {file ? file.name : "Choose a file"}
          </Button>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input data-testid="vault-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="e.g. July rent receipt" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="vault-upload-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !file} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="vault-upload-save">
            {saving ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Uploading…</> : "Add document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
